import { Link, useParams } from 'react-router-dom';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import ShareButton from '@/components/feature/ShareButton';
import { CategoryTag } from '@/components/feature/NewsCard';
import WorkCard from '@/components/feature/WorkCard';
import { getNewsForWork, getWorkById } from '@/utils/content';
import { works } from '@/mocks/works';
import NotFound from '@/pages/NotFound';

export default function WorkDetail() {
  const { id } = useParams();
  const work = getWorkById(id);

  if (!work) {
    return <NotFound />;
  }

  const relatedNews = getNewsForWork(work.id);
  const moreWorks = works.filter((w) => w.id !== work.id).slice(0, 4);

  const infoRows = [
    { label: '类型', value: work.type },
    { label: '发行年份', value: `${work.year} 年` },
    { label: '导演', value: work.director },
    { label: '主演', value: work.cast },
    { label: '时长', value: work.duration },
    { label: '地区', value: work.region },
  ];

  return (
    <>
      <Seo
        title={`${work.title}｜作品详情-深田咏美资讯`}
        description={`深田咏美${work.type}《${work.title}》详细介绍：${work.synopsis.slice(0, 60)}…查看导演、主演、年份等完整资料。`}
        keywords={`深田咏美作品,${work.title},深田咏美${work.type}`}
        ogImage={work.cover}
      />

      <section className="border-b border-background-200 bg-background-100">
        <div className="max-w-6xl mx-auto px-4 py-10 md:px-8 md:py-14">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '影视作品', path: '/works' },
              { label: work.title },
            ]}
          />

          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-[320px_1fr] lg:gap-12">
            <div className="mx-auto w-full max-w-[320px]">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200">
                <img
                  src={work.cover}
                  alt={`${work.title} 封面`}
                  title={`${work.title} 深田咏美影视作品`}
                  className="h-full w-full object-top"
                />
              </div>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-3">
                <span className="rounded-full bg-foreground-950 px-3 py-1 text-xs font-medium text-background-50">
                  {work.type}
                </span>
                <span className="flex items-center gap-1.5 text-sm font-semibold text-accent-600">
                  <i className="ri-star-fill"></i>
                  {work.rating.toFixed(1)}
                </span>
              </div>

              <h1 className="mt-4 font-heading text-3xl font-black text-foreground-950 md:text-4xl">
                {work.title}
              </h1>
              <p className="mt-2 text-sm uppercase tracking-[0.2em] text-secondary-500">{work.titleEn}</p>

              <dl className="mt-7 grid grid-cols-1 gap-x-8 gap-y-4 border-t border-background-200 pt-6 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3">
                    <dt className="w-20 shrink-0 text-sm text-secondary-500">{row.label}</dt>
                    <dd className="text-sm font-medium text-foreground-900">{row.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="mt-6 flex flex-wrap gap-2">
                {work.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-background-200 px-3 py-1 text-xs text-foreground-700">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link
                  to="/works"
                  className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
                >
                  <i className="ri-arrow-left-line"></i>
                  返回作品列表
                </Link>
                <ShareButton />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-6xl mx-auto px-4 py-14 md:px-8 md:py-16">
          <h2 className="mb-6 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>
            作品简介
          </h2>
          <p className="max-w-3xl text-base leading-loose text-foreground-700">{work.synopsis}</p>
        </div>
      </section>

      {relatedNews.length > 0 && (
        <section className="border-y border-background-200 bg-background-100">
          <div className="max-w-6xl mx-auto px-4 py-14 md:px-8 md:py-16">
            <h2 className="mb-6 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              <span className="h-5 w-1 rounded-full bg-primary-500"></span>
              相关资讯
            </h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              {relatedNews.map((n) => (
                <Link
                  key={n.id}
                  to={`/news/${n.id}`}
                  className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-200 cursor-pointer"
                >
                  <div className="h-40 w-full overflow-hidden bg-background-200">
                    <img
                      src={n.thumb}
                      alt={`${n.title} 配图`}
                      title={`${n.title} 深田咏美资讯`}
                      className="h-full w-full object-top transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex flex-1 flex-col p-5">
                    <CategoryTag category={n.category} />
                    <h3 className="mt-3 line-clamp-2 font-heading text-sm font-bold leading-snug text-foreground-950 group-hover:text-primary-700 md:text-base">
                      {n.title}
                    </h3>
                    <span className="mt-3 text-xs text-foreground-400">{n.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="bg-background-50">
        <div className="max-w-6xl mx-auto px-4 py-14 md:px-8 md:py-16">
          <h2 className="mb-6 flex items-center gap-3 font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            <span className="h-5 w-1 rounded-full bg-primary-500"></span>
            更多作品
          </h2>
          <div className="grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4">
            {moreWorks.map((w) => (
              <WorkCard key={w.id} item={w} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}